import type {
  ColumnType,
  RawTableData,
  TransformConfig,
  TransformPreset,
  ColumnStats,
} from '@/types'
import { packBits } from './fileParser'
import type { ParseResult } from './fileParser'

/**
 * Otsu's method for automatic threshold detection on bimodal distributions.
 *
 * Finds the threshold that maximizes the between-class variance of a 256-bin
 * histogram spanning the data range.  Ideal for MTJ resistance data that
 * clusters into two states (parallel / anti-parallel).
 *
 * @param values  Numeric sample values
 * @returns       Optimal threshold value in the data's original scale
 */
export function otsuThreshold(values: number[]): number {
  if (values.length === 0) return 0

  const n = values.length

  // Determine data range
  let min = values[0]
  let max = values[0]
  for (let i = 1; i < n; i++) {
    if (values[i] < min) min = values[i]
    if (values[i] > max) max = values[i]
  }

  // Degenerate case: all values identical
  if (max === min) return min

  const numBins = 256
  const binWidth = (max - min) / numBins

  // Build histogram
  const hist = new Float64Array(numBins)
  for (let i = 0; i < n; i++) {
    let bin = Math.floor((values[i] - min) / binWidth)
    if (bin >= numBins) bin = numBins - 1
    if (bin < 0) bin = 0
    hist[bin]++
  }

  // Normalize to probabilities
  for (let i = 0; i < numBins; i++) {
    hist[i] /= n
  }

  // Compute cumulative sums and cumulative means for efficient sweep
  // w0(t) = sum_{i=0}^{t} hist[i]          (weight of class 0)
  // mu0(t) = (1/w0) * sum_{i=0}^{t} i*hist[i]  (mean of class 0)
  // muTotal = sum_{i=0}^{255} i*hist[i]

  let muTotal = 0
  for (let i = 0; i < numBins; i++) {
    muTotal += i * hist[i]
  }

  let w0 = 0
  let sumI = 0  // running sum of i*hist[i] for class 0
  let maxVariance = -1
  let bestT = 0

  for (let t = 0; t < numBins - 1; t++) {
    w0 += hist[t]
    sumI += t * hist[t]

    if (w0 === 0) continue

    const w1 = 1 - w0
    if (w1 === 0) break

    const mu0 = sumI / w0
    const mu1 = (muTotal - sumI) / w1

    // Between-class variance: w0 * w1 * (mu0 - mu1)^2
    const diff = mu0 - mu1
    const betweenVariance = w0 * w1 * diff * diff

    if (betweenVariance > maxVariance) {
      maxVariance = betweenVariance
      bestT = t
    }
  }

  // Convert bin index back to data scale
  // The threshold sits at the upper edge of the winning bin
  return min + (bestT + 1) * binWidth
}

/**
 * Detect the delimiter used in a text block by trying common delimiters.
 */
export function detectDelimiter(text: string): string {
  const lines = text
    .split(/\r?\n/)
    .filter((l) => {
      const t = l.trim()
      return t !== '' && !t.startsWith('#') && !t.startsWith('%') && !t.startsWith('//') && !t.startsWith(';')
    })
    .slice(0, 10)

  if (lines.length === 0) return ','

  for (const delim of [',', '\t', ';', '|']) {
    const counts = lines.map((l) => l.split(delim).length)
    if (counts[0] > 1 && counts.every((c) => c === counts[0])) {
      return delim
    }
  }

  return ','
}

/**
 * Detect comment prefix from the first 20 lines.
 */
export function detectCommentPrefix(lines: string[]): string | null {
  const candidates = ['#', '%', '//', ';']
  const first20 = lines.slice(0, 20)

  for (const prefix of candidates) {
    const count = first20.filter((l) => l.trimStart().startsWith(prefix)).length
    if (count >= 2) return prefix
  }

  return null
}

/**
 * Detect whether the first row contains headers.
 */
export function detectHeaders(firstRow: string[], secondRow: string[]): boolean {
  if (secondRow.length === 0) return false

  const isNumeric = (s: string): boolean => {
    const trimmed = s.trim()
    return trimmed !== '' && !isNaN(Number(trimmed))
  }

  const isNumericOrEmpty = (s: string): boolean => isNumeric(s) || s.trim() === ''
  // First row must have at least one cell that is truly non-numeric (not just empty)
  const firstHasNonNumeric = firstRow.some((v) => v.trim() !== '' && !isNumeric(v))
  const secondAllNumeric = secondRow.every((v) => isNumericOrEmpty(v))

  if (firstHasNonNumeric && secondAllNumeric) return true
  if (firstRow.every((v) => isNumericOrEmpty(v))) return false

  return false
}

/**
 * Detect the type of a column from its values.
 */
export function detectColumnType(values: string[]): ColumnType {
  const sample = values.slice(0, 200)

  if (sample.every((v) => v.trim() === '')) return 'empty'

  const nonEmpty = sample.filter((v) => v.trim() !== '')
  if (nonEmpty.length === 0) return 'empty'

  let numericCount = 0
  let allBinary = true

  for (const v of nonEmpty) {
    const trimmed = v.trim()
    const num = Number(trimmed)
    if (!isNaN(num) && trimmed !== '') {
      numericCount++
      if (num !== 0 && num !== 1) allBinary = false
    } else {
      allBinary = false
    }
  }

  if (numericCount === nonEmpty.length) {
    return allBinary ? 'binary' : 'numeric'
  }
  if (numericCount === 0) return 'string'
  return 'mixed'
}

/**
 * Parse raw text into a structured table.
 */
export function parseRawTable(
  text: string,
  options?: { delimiter?: string; hasHeader?: boolean; commentPrefix?: string }
): RawTableData {
  // Strip BOM
  let cleaned = text
  if (cleaned.charCodeAt(0) === 0xfeff) {
    cleaned = cleaned.slice(1)
  }

  let lines = cleaned.split(/\r?\n/).filter((l) => l.trim() !== '')

  // Detect or use comment prefix
  const commentPrefix = options?.commentPrefix ?? detectCommentPrefix(lines)

  // Filter comment lines
  if (commentPrefix) {
    lines = lines.filter((l) => !l.trimStart().startsWith(commentPrefix))
  }

  if (lines.length === 0) {
    return {
      headers: null,
      rows: [],
      delimiter: options?.delimiter ?? ',',
      rowCount: 0,
      columnCount: 0,
      columnTypes: [],
      commentPrefix,
    }
  }

  // Detect or use delimiter
  const delimiter = options?.delimiter ?? detectDelimiter(lines.join('\n'))

  // Split lines by delimiter
  const allSplit = lines.map((l) => l.split(delimiter))

  // Find max column count
  const maxCols = Math.max(...allSplit.map((r) => r.length))

  // Pad rows with '' to ensure consistent column count
  const padded = allSplit.map((r) => {
    while (r.length < maxCols) r.push('')
    return r.map((v) => v.trim())
  })

  // Detect or use headers
  let headers: string[] | null = null
  let dataRows: string[][]

  if (options?.hasHeader !== undefined) {
    if (options.hasHeader && padded.length > 0) {
      headers = padded[0]
      dataRows = padded.slice(1)
    } else {
      dataRows = padded
    }
  } else {
    if (padded.length >= 2 && detectHeaders(padded[0], padded[1])) {
      headers = padded[0]
      dataRows = padded.slice(1)
    } else {
      dataRows = padded
    }
  }

  // Detect column types
  const columnTypes: ColumnType[] = []
  for (let c = 0; c < maxCols; c++) {
    const colValues = dataRows.map((r) => r[c] ?? '')
    columnTypes.push(detectColumnType(colValues))
  }

  return {
    headers,
    rows: dataRows,
    delimiter,
    rowCount: dataRows.length,
    columnCount: maxCols,
    columnTypes,
    commentPrefix,
  }
}

/**
 * Compute statistics for a single column.
 */
export function computeColumnStats(rows: string[][], colIndex: number): ColumnStats {
  const values: number[] = []
  let nanCount = 0

  for (const row of rows) {
    const raw = row[colIndex] ?? ''
    const num = Number(raw.trim())
    if (isNaN(num) || raw.trim() === '') {
      nanCount++
    } else {
      values.push(num)
    }
  }

  if (values.length === 0) {
    return {
      min: NaN,
      max: NaN,
      mean: NaN,
      median: NaN,
      stdDev: NaN,
      uniqueCount: 0,
      nanCount,
      histogram: [],
    }
  }

  const sorted = [...values].sort((a, b) => a - b)
  const n = values.length
  const min = sorted[0]
  const max = sorted[n - 1]
  const sum = values.reduce((a, b) => a + b, 0)
  const mean = sum / n

  let median: number
  if (n % 2 === 0) {
    median = (sorted[n / 2 - 1] + sorted[n / 2]) / 2
  } else {
    median = sorted[Math.floor(n / 2)]
  }

  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n
  const stdDev = Math.sqrt(variance)

  const uniqueCount = new Set(values).size

  // Histogram using Sturges' rule
  const numBins = Math.max(1, Math.ceil(1 + 3.322 * Math.log10(n)))
  const binWidth = max === min ? 1 : (max - min) / numBins
  const histogram: { binStart: number; binEnd: number; count: number }[] = []

  for (let i = 0; i < numBins; i++) {
    const binStart = min + i * binWidth
    const binEnd = min + (i + 1) * binWidth
    histogram.push({ binStart, binEnd, count: 0 })
  }

  for (const v of values) {
    let bin = Math.floor((v - min) / binWidth)
    if (bin >= numBins) bin = numBins - 1
    if (bin < 0) bin = 0
    histogram[bin].count++
  }

  return { min, max, mean, median, stdDev, uniqueCount, nanCount, histogram }
}

/**
 * Apply a transformation to raw table data, producing a ParseResult.
 */
export function applyTransform(table: RawTableData, config: TransformConfig): ParseResult {
  // Get working rows: apply skipRows, maxRows, downsample
  let rows = table.rows.slice(config.skipRows)
  if (config.maxRows !== null) {
    rows = rows.slice(0, config.maxRows)
  }
  if (config.downsample > 1) {
    rows = rows.filter((_, i) => i % config.downsample === 0)
  }

  if (config.outputMode === 'continuous') {
    const values: number[] = []
    for (const row of rows) {
      const raw = row[config.selectedColumn] ?? ''
      const num = Number(raw.trim())
      if (!isNaN(num) && raw.trim() !== '') {
        values.push(num)
      }
    }

    if (values.length === 0) {
      throw new Error('No valid numeric values found in selected column.')
    }

    return {
      kind: 'continuous',
      input: {
        data: new Float64Array(values),
        format: 'float-csv',
        sampleCount: values.length,
        fileName: 'transformed-data',
        fileSize: 0,
      },
    }
  }

  // Binary mode
  const rule = config.rules[0]
  if (!rule) {
    throw new Error('No transform rule specified for binary mode.')
  }

  // For median rule, precompute median of source column
  let medianValue: number | undefined
  if (rule.type === 'median') {
    const colValues: number[] = []
    for (const row of rows) {
      const raw = row[rule.sourceColumn] ?? ''
      const num = Number(raw.trim())
      if (!isNaN(num) && raw.trim() !== '') {
        colValues.push(num)
      }
    }
    if (colValues.length === 0) {
      throw new Error('No valid numeric values for median computation.')
    }
    const sorted = [...colValues].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    medianValue =
      sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
  }

  // For auto-otsu rule, precompute Otsu threshold from source column
  let otsuValue: number | undefined
  if (rule.type === 'auto-otsu') {
    const colValues: number[] = []
    for (const row of rows) {
      const raw = row[rule.sourceColumn] ?? ''
      const num = Number(raw.trim())
      if (!isNaN(num) && raw.trim() !== '') {
        colValues.push(num)
      }
    }
    if (colValues.length === 0) {
      throw new Error('No valid numeric values for Otsu threshold computation.')
    }
    otsuValue = otsuThreshold(colValues)
  }

  const bits: number[] = []

  for (const row of rows) {
    const raw = row[rule.sourceColumn] ?? ''
    const num = Number(raw.trim())

    if (isNaN(num) && raw.trim() === '' && rule.type !== 'passthrough') continue
    if (isNaN(num) && rule.type !== 'passthrough') continue

    switch (rule.type) {
      case 'threshold': {
        if (rule.operator === undefined || rule.threshold === undefined) {
          throw new Error('Threshold rule requires operator and threshold.')
        }
        let result: boolean
        switch (rule.operator) {
          case '>': result = num > rule.threshold; break
          case '>=': result = num >= rule.threshold; break
          case '<': result = num < rule.threshold; break
          case '<=': result = num <= rule.threshold; break
          case '==': result = num === rule.threshold; break
          case '!=': result = num !== rule.threshold; break
        }
        bits.push(result ? 1 : 0)
        break
      }
      case 'range': {
        if (rule.rangeMin === undefined || rule.rangeMax === undefined) {
          throw new Error('Range rule requires rangeMin and rangeMax.')
        }
        bits.push(num >= rule.rangeMin && num <= rule.rangeMax ? 1 : 0)
        break
      }
      case 'median': {
        bits.push(num > medianValue! ? 1 : 0)
        break
      }
      case 'column-compare': {
        if (rule.compareColumn === undefined) {
          throw new Error('Column-compare rule requires compareColumn.')
        }
        const rawCompare = row[rule.compareColumn] ?? ''
        const numCompare = Number(rawCompare.trim())
        if (isNaN(numCompare)) continue
        bits.push(num > numCompare ? 1 : 0)
        break
      }
      case 'auto-otsu': {
        bits.push(num > otsuValue! ? 1 : 0)
        break
      }
      case 'passthrough': {
        const intVal = parseInt(raw.trim(), 10)
        if (intVal === 0 || intVal === 1) {
          bits.push(intVal)
        }
        break
      }
    }
  }

  if (bits.length === 0) {
    throw new Error('No valid bits produced by transform.')
  }

  const { data, bitLength } = packBits(bits)

  return {
    kind: 'binary',
    input: {
      data,
      bitLength,
      format: 'csv',
      sampleCount: bitLength,
      fileName: 'transformed-data',
      fileSize: 0,
    },
  }
}

/**
 * Quick check if text contains multi-column data.
 */
export function isMultiColumnData(text: string): boolean {
  const lines = text
    .split(/\r?\n/)
    .filter((l) => {
      const t = l.trim()
      return t !== '' && !t.startsWith('#') && !t.startsWith('%') && !t.startsWith('//') && !t.startsWith(';')
    })
    .slice(0, 5)

  if (lines.length < 2) return false

  for (const delim of [',', '\t', ';', '|']) {
    const counts = lines.map((l) => l.split(delim).length)
    if (counts[0] > 1 && counts.every((c) => c === counts[0])) {
      return true
    }
  }

  return false
}

/**
 * Get available transformation presets.
 */
export function getPresets(): TransformPreset[] {
  return [
    {
      id: 'mtj-resistance',
      label: 'MTJ Resistance \u2192 Binary',
      description: 'Threshold a resistance column to produce binary output',
      outputMode: 'binary',
      ruleType: 'threshold',
      requiresInput: [
        { field: 'threshold', label: 'Resistance threshold', placeholder: 'e.g. 5000' },
      ],
    },
    {
      id: 'mtj-otsu',
      label: 'MTJ Auto-Threshold (Otsu)',
      description: 'Automatically find the optimal threshold for bimodal MTJ resistance data using Otsu\'s method',
      outputMode: 'binary',
      ruleType: 'auto-otsu',
      requiresInput: [],
    },
    {
      id: 'voltage-telegraph',
      label: 'Voltage Telegraph Signal',
      description: 'Split at median voltage for binary output',
      outputMode: 'binary',
      ruleType: 'median',
      requiresInput: [],
    },
    {
      id: 'binary-column',
      label: 'Binary Column (0/1)',
      description: 'Pass through a column that already contains 0/1 values',
      outputMode: 'binary',
      ruleType: 'passthrough',
      requiresInput: [],
    },
    {
      id: 'continuous-values',
      label: 'Continuous Values',
      description: 'Extract a column of floating-point values for Gaussian testing',
      outputMode: 'continuous',
      ruleType: 'passthrough',
      requiresInput: [],
    },
    {
      id: 'differential-comparison',
      label: 'Differential Comparison',
      description: 'Compare two columns: output 1 when column A > column B',
      outputMode: 'binary',
      ruleType: 'column-compare',
      requiresInput: [],
    },
  ]
}
